import React from "react";
import type { GradeMotorizationType } from "../../../../../../common-deprecated/types/GradeType";
import * as Flex from "../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import { BnbLabel, useBnbOptionalLabel } from "../../../utils/labels";
import * as Button from "../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as Text from "../../../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import * as Type from "../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import EngineSpecs from "./EngineSpecs";
import { compareAddCar } from "../../../../../../common-deprecated/features/compare/redux/actions/NewCarCompareExternalDataActions";
import * as Styles from "./styles/Motorization";
import Price from "../common/price/Price";
import { getEcoBadgeUrl } from "../../../../../../common-deprecated/utils";
import { MODAL_ENGINE_DETAIL } from "../../../utils/modalConstants";
import { IconWrapper } from "../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Icon, { IconTradeIn } from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import {
    getEcoBadgeType,
    getWLTPLabel,
    isRTL,
} from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { EcoBadge } from "../../../../../../common-deprecated/styles/toyota/Badge";
import { getMotorizationEngineText } from "../../../utils/engineUtils";
import { MODAL_CONTENT } from "../../../../../../common-deprecated/utils/modalConstants";
import { trackAddCompare, trackEngineViewInfo, trackNavGoToStep } from "../../../utils/tracking";
import EcoBonusPromotion from "../../../../../../common-deprecated/components/aem/EcoBonusPromotion";
import { getEcoBonusPromotions } from "../../../../../../common-deprecated/utils/promotionUtils";
import type { PricePromotionType } from "../../../../../../common-deprecated/types/CommonTypes";
import FuelTypeLabel from "../../../../../../common-deprecated/styles/v2/toyota/components/FuelTypeLabel";
import ButtonPrimary from "../../../../../../common-deprecated/components/aem/toyota/ButtonPrimary";
import Spacer from "../../../../../../common-deprecated/styles/v2/toyota/utilities/Spacer";
import ButtonTertiary from "../../../../../../common-deprecated/components/aem/toyota/ButtonTertiary";
import { getEcoTagType } from "../../../../../shared-logic/utils/typedUtils";
import ButtonQuaternary from "../../../../../../common-deprecated/components/aem/toyota/ButtonQuaternary";
import AvailabilityBadges from "../../../../../../common-deprecated/components/aem/toyota/availability-badges/AvailabilityBadges";
import useStepUrl from "../../../hooks/shared/actions/useStepUrl";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type MotorizationType = {
    motorization: GradeMotorizationType;
    gradeHasMultipleEngines: boolean;
};
const Motorization = (props: MotorizationType): JSX.Element => {
    const { motorization, gradeHasMultipleEngines } = props;
    const { engine, transmission, wheeldrive, specs, price, carID, submodelId, soldOut } = motorization;

    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const grade = useBnbSelector((state) => state.grade);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const ecoBonus = useBnbSelector((state) =>
        getEcoBonusPromotions<PricePromotionType>(state.items.promotions, motorization.price.promotions),
    );

    const customizeStep = useStepUrl(NavStep.Customize, { carId: carID }, undefined, () =>
        trackNavGoToStep(NavStep.Engine, NavStep.Customize),
    );

    const fuelType = getEcoTagType(engine);
    const ecoBadge = getEcoBadgeType(commonSettings, fuelType, grade.model.id);
    const ecoBadgeUrl = getEcoBadgeUrl(ecoBadge, commonSettings);
    const [noAccessoriesDisclaimer, genericDisclaimerTitle] = useBnbOptionalLabel([
        "genericEngineDisclaimer",
        "genericDisclaimerTitle",
    ]);

    const engineText = getMotorizationEngineText(commonSettings, engine);

    const fullName = `${grade.model.name} ${grade.name} ${engine.name} ${transmission.name}:`;

    return (
        <Styles.Wrapper>
            {/* Header */}
            <Flex.Row
                justifyContent="space-between"
                alignItems={isRTL(commonSettings) ? "flex-end" : "flex-start"}
                flexWrap="nowrap"
            >
                <Flex.Col>
                    <Styles.OptionTitle headingType="zeta" mb={0}>
                        {engine.name} <FuelTypeLabel type={fuelType}>{engineText}</FuelTypeLabel>
                    </Styles.OptionTitle>
                    <Type.SmallText>{`${transmission.name} (${wheeldrive.name})`}</Type.SmallText>
                </Flex.Col>
                <Flex.Col>{ecoBadgeUrl && <EcoBadge imageUrl={ecoBadgeUrl} />}</Flex.Col>
            </Flex.Row>

            <Flex.Row>
                <Flex.Col>
                    <EcoBonusPromotion ecoBonus={ecoBonus} />
                    <Price
                        price={price}
                        financeInfo={motorization.finance}
                        carInternalCode={grade.model.code}
                        cashPriceDisclaimer={grade.model.labels}
                    />
                    {!!noAccessoriesDisclaimer && (
                        <Button.Link
                            type="button"
                            hasIconRight
                            onClick={() =>
                                dispatch(showModal({ type: MODAL_CONTENT, content: noAccessoriesDisclaimer }))
                            }
                        >
                            {genericDisclaimerTitle && <Text.Button>{genericDisclaimerTitle}</Text.Button>}
                            <IconWrapper aria-hidden="true">
                                <Icon variant="info" />
                            </IconWrapper>
                        </Button.Link>
                    )}
                    <AvailabilityBadges
                        isSoldOut={soldOut}
                        isAvailableForOnlineSales={grade.availableForOnlineSales}
                        isAvailableForOnlineLeasing={grade.availableForOnlineLeasing}
                    />
                </Flex.Col>
            </Flex.Row>

            {/* Compare footer */}
            <Styles.Footer>
                {/* Content */}
                <EngineSpecs
                    specs={specs}
                    engineDisclaimer={getWLTPLabel(commonSettings, engine.certification, engine.electric)}
                    electric={engine.electric}
                />
                <Spacer mb={3}>
                    <ButtonTertiary
                        onClick={() => {
                            trackEngineViewInfo();
                            dispatch(showModal({ type: MODAL_ENGINE_DETAIL, motorization }));
                        }}
                        aria-label={fullName}
                    >
                        <Text.Button>
                            <BnbLabel label="moreInfo" />
                        </Text.Button>
                    </ButtonTertiary>
                </Spacer>
                <ButtonPrimary
                    fullWidth
                    aria-hidden="true"
                    tabIndex={-1}
                    aria-label={fullName}
                    href={customizeStep.href}
                    onClick={customizeStep.onClick}
                >
                    <BnbLabel
                        label={gradeHasMultipleEngines ? "chooseThisEngine" : "thisIsYourEngineButton"}
                        fallbackLabel="chooseThisEngine"
                    />
                </ButtonPrimary>
                {gradeHasMultipleEngines && (
                    <Flex.Col width={1} p={0} mt="12px" display="flex" justifyContent="center">
                        <ButtonQuaternary
                            onClick={(event) => {
                                event.preventDefault();
                                trackAddCompare(NavStep.Grades);
                                const compareMotorization = {
                                    engineId: motorization.engine.id,
                                    transmissionId: transmission.id,
                                    wheeldriveId: motorization.wheeldrive.id,
                                    // Names are not required, these are resolved in the backend.
                                    engineName: "",
                                    transmissionName: "",
                                    wheeldriveName: "",
                                };

                                const ids = {
                                    model: grade.model.code,
                                    gradeId: grade.id,
                                    bodyTypeId: grade.bodyType.id,
                                    submodelId,
                                };
                                dispatch(compareAddCar(ids, compareMotorization, -1));
                            }}
                            aria-label={fullName}
                            hasIconRight
                            fullWidth
                        >
                            <IconWrapper>
                                <IconTradeIn />
                            </IconWrapper>
                            <BnbLabel label="compareAdd" />
                        </ButtonQuaternary>
                    </Flex.Col>
                )}
            </Styles.Footer>
        </Styles.Wrapper>
    );
};

export default Motorization;
